import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, apiClient } from "@/App";
import { toast } from "sonner";
import { 
  ArrowRight, 
  Shield, 
  Mail, 
  Phone, 
  Lock, 
  User, 
  Eye, 
  EyeOff,
  ChevronLeft,
  Sparkles
} from "lucide-react";

const Login = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  
  // Auth mode: "login" or "register"
  const [authMode, setAuthMode] = useState("login");
  // Auth method: "email" or "phone"
  const [authMethod, setAuthMethod] = useState("email");
  
  // Form states
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Phone OTP states
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [testOtp, setTestOtp] = useState("");

  // If already logged in, redirect to dashboard
  if (user) {
    navigate("/dashboard");
    return null;
  }

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    
    if (!password) {
      toast.error("Please enter your password");
      return;
    }
    
    if (authMode === "register" && !name) {
      toast.error("Please enter your name");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      const endpoint = authMode === "login" ? "/auth/login" : "/auth/register";
      const payload = authMode === "login" 
        ? { email, password }
        : { email, password, name };
      
      const response = await apiClient.post(endpoint, payload);
      
      // Store session token
      if (response.data.session_token) {
        localStorage.setItem("be_session_token", response.data.session_token);
      }
      
      setUser(response.data.user);
      
      if (authMode === "register") {
        toast.success(`Welcome to BE! You earned ${response.data.points_earned || 500} bonus points!`);
      } else {
        toast.success(response.data.message || "Welcome back!");
      }
      
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async () => {
    if (!phone) {
      toast.error("Please enter your phone number");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await apiClient.post("/auth/phone/request-otp", { phone });
      setOtpSent(true);
      setTestOtp(response.data.otp_for_testing || ""); // For testing - remove in production
      toast.success("OTP sent to your phone!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await apiClient.post("/auth/phone/verify-otp", { phone, otp });
      
      if (response.data.requires_registration) {
        // New user - need to complete registration
        setPhoneVerified(true);
        toast.success("Phone verified! Please complete your registration.");
      } else {
        // Existing user - logged in
        if (response.data.session_token) {
          localStorage.setItem("be_session_token", response.data.session_token);
        }
        setUser(response.data.user);
        toast.success(response.data.message || "Welcome back!");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleCompletePhoneRegistration = async (e) => {
    e.preventDefault();
    
    if (!name) {
      toast.error("Please enter your name");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await apiClient.post("/auth/phone/complete-registration", {
        phone,
        name,
        password: password || null
      });
      
      if (response.data.session_token) {
        localStorage.setItem("be_session_token", response.data.session_token);
      }
      
      setUser(response.data.user);
      toast.success(`Welcome to BE! You earned ${response.data.points_earned || 500} bonus points!`);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const resetPhoneFlow = () => {
    setOtpSent(false);
    setPhoneVerified(false);
    setOtp("");
    setTestOtp("");
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_fitechglobal/artifacts/7g0ygqsm_be%20logo.jpg" 
            alt="BE Lifestyle" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <h1 className="font-heading font-bold text-3xl mb-2">
            {authMode === "login" ? "Welcome Back" : "Join BE"}
          </h1>
          <p className="text-muted-foreground">
            {authMode === "login" 
              ? "Sign in to continue your journey" 
              : "Create an account and get 500 bonus points"}
          </p>
        </div>

        {/* Auth Card */}
        <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
          {/* Auth Method Tabs */}
          <Tabs value={authMethod} onValueChange={setAuthMethod} className="mb-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="email" data-testid="email-tab">
                <Mail className="w-4 h-4 mr-2" />
                Email
              </TabsTrigger>
              <TabsTrigger value="phone" data-testid="phone-tab">
                <Phone className="w-4 h-4 mr-2" />
                Phone
              </TabsTrigger>
            </TabsList>

            {/* Email Auth */}
            <TabsContent value="email" className="mt-6">
              <form onSubmit={handleEmailAuth} className="space-y-4">
                <AnimatePresence mode="wait">
                  {authMode === "register" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-2"
                    >
                      <Label htmlFor="name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="name"
                          placeholder="Enter your name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="pl-10"
                          data-testid="name-input"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {authMode === "register" && (
                    <p className="text-xs text-muted-foreground">Minimum 6 characters</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-full py-6"
                  disabled={loading}
                  data-testid="email-submit-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      {authMode === "login" ? "Sign In" : "Create Account"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>

            {/* Phone Auth */}
            <TabsContent value="phone" className="mt-6">
              <AnimatePresence mode="wait">
                {!otpSent ? (
                  // Step 1: Enter phone number
                  <motion.div
                    key="phone-input"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+254 7XX XXX XXX"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="pl-10"
                          data-testid="phone-input"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        We'll send you a 6-digit verification code
                      </p>
                    </div>

                    <Button
                      onClick={handleRequestOTP}
                      className="w-full rounded-full py-6"
                      disabled={loading}
                      data-testid="request-otp-btn"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          Send OTP
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </motion.div>
                ) : !phoneVerified ? (
                  // Step 2: Enter OTP
                  <motion.div
                    key="otp-input"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <button
                      onClick={resetPhoneFlow}
                      className="flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Change number
                    </button>

                    <div className="space-y-2">
                      <Label htmlFor="otp">Verification Code</Label>
                      <Input
                        id="otp"
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="text-center text-2xl tracking-widest font-mono"
                        maxLength={6}
                        data-testid="otp-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Code sent to {phone}
                      </p>
                      {testOtp && (
                        <p className="text-xs text-secondary font-mono bg-secondary/10 p-2 rounded">
                          Test OTP: {testOtp}
                        </p>
                      )}
                    </div>

                    <Button
                      onClick={handleVerifyOTP}
                      className="w-full rounded-full py-6"
                      disabled={loading || otp.length !== 6}
                      data-testid="verify-otp-btn"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          Verify Code
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>

                    <button
                      onClick={handleRequestOTP}
                      disabled={loading}
                      className="w-full text-sm text-muted-foreground hover:text-primary"
                    >
                      Didn't receive code? Resend
                    </button>
                  </motion.div>
                ) : (
                  // Step 3: Complete registration (new user)
                  <motion.div
                    key="complete-registration"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <form onSubmit={handleCompletePhoneRegistration} className="space-y-4">
                      <div className="flex items-center gap-2 text-sm text-secondary mb-4">
                        <Sparkles className="w-4 h-4" />
                        Phone verified! Complete your profile.
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone-name">Full Name</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="phone-name"
                            placeholder="Enter your name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="pl-10"
                            data-testid="phone-name-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone-password">Password (optional)</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="phone-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Set a password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="pl-10 pr-10"
                            data-testid="phone-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Optional - for logging in with password later
                        </p>
                      </div>

                      <Button
                        type="submit"
                        className="w-full rounded-full py-6"
                        disabled={loading}
                        data-testid="complete-registration-btn"
                      >
                        {loading ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            Complete Registration
                            <Sparkles className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </TabsContent>
          </Tabs>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          {/* Google Auth */}
          <Button
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full rounded-full py-6"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          {/* Toggle Auth Mode */}
          <div className="mt-6 text-center text-sm">
            {authMode === "login" ? (
              <p className="text-muted-foreground">
                Don't have an account?{" "}
                <button
                  onClick={() => setAuthMode("register")}
                  className="text-primary font-medium hover:underline"
                  data-testid="switch-to-register"
                >
                  Sign up
                </button>
              </p>
            ) : (
              <p className="text-muted-foreground">
                Already have an account?{" "}
                <button
                  onClick={() => setAuthMode("login")}
                  className="text-primary font-medium hover:underline"
                  data-testid="switch-to-login"
                >
                  Sign in
                </button>
              </p>
            )}
          </div>
        </div>

        {/* Security Badge */}
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Shield size={16} />
          <span>Your data is secure and encrypted</span>
        </div>

        {/* Back Link */}
        <div className="mt-4 text-center">
          <Button variant="ghost" onClick={() => navigate("/")} data-testid="back-home-btn">
            ← Back to Home
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
